/**
 * Template Resolver
 *
 * Implements the template resolution chain:
 *   1. User-uploaded template (IndexedDB via TemplateStore)
 *   2. Brand default template (static asset in public/brands/)
 *   3. null → fall back to existing programmatic export
 */

import { logger } from "@/telemetry";
import { useTemplateStore } from "@/stores/TemplateStore";
import {
  getDefaultTemplatePath,
  getTemplatesForFormat,
  resolveBrandKey,
  type BrandTemplate,
  type TemplateFormat,
} from "@/config/brandTemplates";

export interface ResolvedTemplate {
  /** The template binary data */
  data: ArrayBuffer;
  /** Where the template came from */
  source: "user" | "brand";
  /** Human-readable name for UI display */
  name: string;
}

/**
 * Resolve the best available template for a given format and brand.
 *
 * Resolution order:
 *   1. User override (IndexedDB) — if the user uploaded a custom template
 *   2. Brand default (static asset) — if the brand has a default template
 *   3. null — no template found, fall back to programmatic export
 *
 * @param format - "word", "pptx", or "xlsx"
 * @param brandName - The brand name (e.g. "NGV", "WeBuild AI")
 * @returns The resolved template or null
 */
export async function resolveTemplate(
  format: TemplateFormat,
  brandName: string,
): Promise<ResolvedTemplate | null> {
  const store = useTemplateStore.getState();

  // Check if templates are disabled by user preference
  if (!store.useTemplateForExports) {
    return null;
  }

  // 1. Check user override (IndexedDB)
  const metadata = store.getTemplateMetadata(format);

  if (metadata) {
    try {
      const data = await store.getTemplateData(format);

      if (data) {
        logger.info(`Using user-uploaded ${format} template: ${metadata.name}`);

        return {
          data,
          source: "user",
          name: metadata.name,
        };
      }
    } catch (error) {
      logger.warn(`Failed to load user ${format} template, falling back to brand default`, {
        error: String(error),
      });
    }
  }

  // 2. Check brand default (static asset)
  const brandKey = resolveBrandKey(brandName);

  if (brandKey) {
    const templatePath = getDefaultTemplatePath(brandKey, format);

    if (templatePath) {
      try {
        const response = await fetch(templatePath);

        if (response.ok) {
          const data = await response.arrayBuffer();
          const fileName = templatePath.split("/").pop() || `${brandKey}-template`;

          logger.info(`Using brand ${format} template: ${fileName}`);

          return {
            data,
            source: "brand",
            name: fileName,
          };
        }
      } catch (error) {
        logger.warn(`Failed to fetch brand ${format} template from ${templatePath}`, {
          error: String(error),
        });
      }
    }
  }

  // 3. No template found
  return null;
}

/**
 * Resolve a specific template by ID from the brand gallery.
 */
export async function resolveTemplateById(
  brandName: string,
  templateId: string,
): Promise<ResolvedTemplate | null> {
  const brandKey = resolveBrandKey(brandName);

  if (!brandKey) return null;

  const { getTemplateById } = await import("@/config/brandTemplates");
  const template = getTemplateById(brandKey, templateId);

  if (!template) return null;

  try {
    const response = await fetch(template.path);

    if (response.ok) {
      const data = await response.arrayBuffer();

      return {
        data,
        source: "brand",
        name: template.name,
      };
    }
  } catch (error) {
    logger.warn(`Failed to fetch template ${templateId}`, { error: String(error) });
  }

  return null;
}

/**
 * Check if a template is available for a given format and brand
 * without actually loading the binary data. Useful for UI indicators.
 */
export function hasTemplateAvailable(format: TemplateFormat, brandName: string): boolean {
  const store = useTemplateStore.getState();

  if (!store.useTemplateForExports) {
    return false;
  }

  // Check user override
  const metadata = store.getTemplateMetadata(format);

  if (metadata) return true;

  // Check brand default
  const brandKey = resolveBrandKey(brandName);

  if (brandKey && getDefaultTemplatePath(brandKey, format)) {
    return true;
  }

  return false;
}

/**
 * Verify that a brand default template file actually exists on the server.
 * Uses a HEAD request to avoid downloading the full binary.
 * Returns the template filename if it exists, or null if not.
 */
export async function verifyBrandDefaultExists(
  format: TemplateFormat,
  brandName: string,
): Promise<string | null> {
  const brandKey = resolveBrandKey(brandName);

  if (!brandKey) return null;

  const templatePath = getDefaultTemplatePath(brandKey, format);

  if (!templatePath) return null;

  try {
    const response = await fetch(templatePath, { method: "HEAD" });

    if (response.ok) {
      return templatePath.split("/").pop() || null;
    }
  } catch {
    // Network error or file not found — treat as no default
  }

  return null;
}

/**
 * Get the display name of the active template for a given format and brand.
 * Returns null if no template is available.
 */
export function getActiveTemplateName(format: TemplateFormat, brandName: string): string | null {
  const store = useTemplateStore.getState();

  if (!store.useTemplateForExports) {
    return null;
  }

  // Check user override
  const metadata = store.getTemplateMetadata(format);

  if (metadata) return metadata.name;

  // Check brand default
  const brandKey = resolveBrandKey(brandName);

  if (brandKey) {
    const templatePath = getDefaultTemplatePath(brandKey, format);

    if (templatePath) {
      return templatePath.split("/").pop() || null;
    }
  }

  return null;
}

/**
 * Get all available templates for a given format and brand (for gallery UI).
 */
export function getAvailableTemplates(format: TemplateFormat, brandName: string): BrandTemplate[] {
  const brandKey = resolveBrandKey(brandName);

  if (!brandKey) return [];

  return getTemplatesForFormat(brandKey, format);
}
